import { useState } from 'react';
import type { Dispatch } from 'react';
import { MAX_INPUTS, inputLabel } from '../utils/datasetMappingUtils';
import type { DatasetFormAction, DatasetFormState } from '../utils/datasetFormUtils';

/**
 * Which input the dataset editor shows, and the inputs whose pattern text is not valid JSON. Adding an input selects
 * it; removing one selects the input before it, and the inputs after it move up a position.
 */
export function useDatasetInputTabs(form: DatasetFormState, dispatch: Dispatch<DatasetFormAction>) {
  const [selected, setSelected] = useState(0);
  const [invalidPatterns, setInvalidPatterns] = useState<number[]>([]);

  const addInput = () => {
    if (form.inputs.length >= MAX_INPUTS) return;
    dispatch({ type: 'ADD_INPUT' });
    setSelected(form.inputs.length);
  };

  const removeInput = (input: number) => {
    dispatch({ type: 'REMOVE_INPUT', input });
    setInvalidPatterns((now) => now.filter((n) => n !== input).map((n) => (n > input ? n - 1 : n)));
    setSelected(Math.max(0, input - 1));
  };

  const setPatternValid = (valid: boolean) =>
    setInvalidPatterns((now) => (valid ? now.filter((n) => n !== selected) : [...new Set([...now, selected])]));

  /** The pattern texts that are not valid JSON, each named after its input when there are several. */
  const patternProblems = () => {
    const several = form.inputs.length > 1;
    return invalidPatterns.map((n) => `${several ? `${inputLabel(n)}: ` : ''}Event pattern is not valid JSON`);
  };

  return { selected, input: form.inputs[selected], setSelected, addInput, removeInput, setPatternValid, patternProblems };
}
